import { ICardApplicationForm } from '~/interfaces/cardApplication';
import {
  DataPointsShared,
  ILoanApplicationForm,
} from '~/interfaces/loanApplication';
import { CardDataPointsShared } from '~/schemas/DTO/cardApplicationSchemas';
import { AppDispatch } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';

const getCardApplicationFormFieldValueByDataPointShared = (
  dataPointName: keyof CardDataPointsShared,
  form: ICardApplicationForm
) => {
  switch (dataPointName) {
    case 'company':
      return (
        form.organizationNumber ||
        form.creditReportProvider?.creditReportProviderNumber
      );
    case 'turnover':
      return form.turnover;
    case 'cardPurpose':
      return form.purposeOfCard;
    case 'cardPurposeReason':
      return form.cardPurposeManual;
    case 'applicantEmail':
      return form.applicant.email;
    case 'applicantPhone':
      return form.applicant.phone;
    case 'applicantPersonalNumber':
      return form.applicant.personalNumber;
    case 'applicantDateOfBirth':
      return form.applicant.dateOfBirth;
    case 'applicantFullName':
      return form.applicant.firstName && form.applicant.lastName;
    case 'applicantAddress':
      return form.applicant.address;
    default: {
      // Exhaustiveness check: TypeScript will error if a case is missing
      const _exhaustiveCheck: never = dataPointName;
      return _exhaustiveCheck;
    }
  }
};

/**
 * Determine which data points need to be shared based on the form values and already shared data points.
 * It should only be marked as to be shared if it has a value in the form and has not yet been shared.
 */
export const determineNewDataPointsToShareForCardApplication = (
  form: ICardApplicationForm,
  dataPointsShared: CardDataPointsShared
): Partial<CardDataPointsShared> => {
  const newDataPointsToShare: Partial<CardDataPointsShared> = {};
  const typedDataPointsShared = Object.keys(dataPointsShared) as Array<
    keyof CardDataPointsShared
  >;

  for (const key of typedDataPointsShared) {
    if (!dataPointsShared[key]) {
      const formValue = getCardApplicationFormFieldValueByDataPointShared(
        key,
        form
      );

      if (formValue) {
        newDataPointsToShare[key] = true;
      }
    }
  }

  return newDataPointsToShare;
};

/**
 * Dispatch tracking events for shared data points.
 */
export const dispatchCardApplicationDataPointTrackingEvents = (
  dispatch: AppDispatch,
  dataPointsToShare: Partial<CardDataPointsShared>
) => {
  for (const dataPointName of Object.keys(dataPointsToShare)) {
    const actionName = `${dataPointName}_data_point_shared`;

    dispatch(
      trackCardApplicationActions({
        actionName,
      })
    );
  }
};

const getLoanApplicationFormFieldValueByDataPointShared = (
  dataPointName: keyof DataPointsShared,
  form: ILoanApplicationForm
) => {
  switch (dataPointName) {
    case 'turnover':
      return form.turnover;
    case 'incomeReceivedInForeignCurrencies':
      return typeof form.incomeReceivedInForeignCurrencies === 'boolean'
        ? form.incomeReceivedInForeignCurrencies
        : undefined;
    case 'loanPurpose':
      return form.purposeOfLoan;
    case 'cardOptIn':
      return typeof form.cardOptIn === 'boolean' ? form.cardOptIn : undefined;
    case 'applicantEmail':
      return form.applicant.email;
    case 'applicantPhone':
      return form.applicant.phone;
    case 'applicantPersonalNumber':
      return form.applicant.personalNumber;
    case 'applicantDateOfBirth':
      return form.applicant.dateOfBirth;
    case 'applicantFullName':
      return form.applicant.firstName && form.applicant.lastName;
    case 'applicantAddress':
      return form.applicant.address;
    default: {
      // Exhaustiveness check: TypeScript will error if a case is missing
      const _exhaustiveCheck: never = dataPointName;
      return _exhaustiveCheck;
    }
  }
};

/**
 * Determine which loan application data points need to be shared based on
 * the form values and already shared data points. It should only be marked
 * as to be shared if it has a value in the form and has not yet been shared.
 */
export const determineNewDataPointsToShareForLoanApplication = (
  form: ILoanApplicationForm,
  dataPointsShared: DataPointsShared
): Partial<DataPointsShared> => {
  const newDataPointsToShare: {
    -readonly [K in keyof DataPointsShared]?: boolean;
  } = {};
  const typedDataPointsShared = Object.keys(dataPointsShared) as Array<
    keyof DataPointsShared
  >;

  for (const key of typedDataPointsShared) {
    if (!dataPointsShared[key]) {
      const formValue = getLoanApplicationFormFieldValueByDataPointShared(
        key,
        form
      );

      // `cardOptIn` / `incomeReceivedInForeignCurrencies` are boolean
      // fields where `false` is a meaningful, already-shared choice, not
      // an absent value — so they can't use plain truthiness like the
      // other (string/date/object) data points.
      const isBooleanDataPoint =
        key === 'cardOptIn' || key === 'incomeReceivedInForeignCurrencies';
      const hasValue = isBooleanDataPoint
        ? typeof formValue === 'boolean'
        : !!formValue;

      if (hasValue) {
        newDataPointsToShare[key] = true;
      }
    }
  }

  return newDataPointsToShare;
};

const toSnakeCase = (value: string) =>
  value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

/**
 * Dispatch GTM tracking events for shared loan application data points.
 *
 * Emits a single `data_point_shared` action with the specific data point in
 * the `dataPoint` property, rather than encoding the data point into the
 * action name. Adding a new data point then needs no analytics-side changes:
 * it shows up as a new `dataPoint` value that can be filtered and broken
 * down like any other property.
 */
export const dispatchLoanApplicationDataPointTrackingEvents = (
  dispatch: AppDispatch,
  dataPointsToShare: Partial<DataPointsShared>
) => {
  for (const dataPointName of Object.keys(dataPointsToShare)) {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'data_point_shared',
        extraContext: {
          dataPoint: toSnakeCase(dataPointName),
        },
      })
    );
  }
};
